import { useEffect, useState, type ReactNode } from 'react';
import type { BillingTiming, Product, PsaClass, UpdateProductInput } from '@catalyst-psa/shared';
import {
  BILLING_TIMINGS,
  BILLING_TIMING_LABELS,
  DEFAULT_PAGE_SIZE,
  PSA_CLASSES,
  PSA_CLASS_LABELS,
} from '@catalyst-psa/shared';

import { useAuth } from '../../auth/authContext.js';
import { PageHeader } from '../../components/layout/PageHeader.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { DataState } from '../../components/ui/DataState.js';
import { Icon } from '../../components/ui/Icon.js';
import { Pagination } from '../../components/ui/Pagination.js';
import { useServiceCategoryOptions } from '../service-categories/api.js';
import { ProductFormModal } from './ProductFormModal.js';
import { useProducts, useUpdateProduct } from './api.js';

const FILTER_INPUT =
  'w-full rounded border border-slate-300 px-2 py-1 text-xs font-normal normal-case text-slate-700 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500';

interface ProductFilters {
  sku: string;
  name: string;
  psaClass: string;
  customerDescription: string;
  serviceCategoryId: string;
  qboClass: string;
  billingTiming: string;
  isActive: string;
}

const EMPTY_FILTERS: ProductFilters = {
  sku: '',
  name: '',
  psaClass: '',
  customerDescription: '',
  serviceCategoryId: '',
  qboClass: '',
  billingTiming: '',
  isActive: '',
};

const INLINE_CONTROL =
  'w-full rounded border border-slate-300 px-1 py-1 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500';

/** Click-to-edit text cell. Commits on blur/Enter, cancels on Escape. */
function TextCell({
  value,
  canEdit,
  saving,
  onCommit,
  required = false,
  mono = false,
  bold = false,
  align = 'left',
  placeholder,
  truncate = false,
}: {
  value: string;
  canEdit: boolean;
  saving: boolean;
  onCommit: (next: string) => void;
  required?: boolean;
  mono?: boolean;
  bold?: boolean;
  align?: 'left' | 'right';
  placeholder?: string;
  truncate?: boolean;
}): JSX.Element {
  const [editing, setEditing] = useState(false);
  const textClass = `${mono ? 'font-mono text-xs ' : ''}${bold ? 'font-medium text-slate-900' : 'text-slate-600'}`;
  if (editing && canEdit) {
    return (
      <input
        autoFocus
        defaultValue={value}
        placeholder={placeholder}
        inputMode={align === 'right' ? 'decimal' : undefined}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setEditing(false);
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        }}
        onBlur={(e) => {
          const next = e.target.value;
          if (required && next.trim() === '') {
            setEditing(false);
            return;
          }
          if (next !== value) onCommit(next);
          setEditing(false);
        }}
        className={`${INLINE_CONTROL} ${align === 'right' ? 'text-right' : ''}`}
      />
    );
  }
  const label = value || '—';
  const layout = `${align === 'right' ? 'text-right' : 'text-left'} ${truncate ? 'block max-w-xs truncate' : ''}`;
  if (!canEdit) {
    return (
      <span className={`${textClass} ${layout}`} title={truncate ? value : undefined}>
        {label}
      </span>
    );
  }
  return (
    <button
      type="button"
      disabled={saving}
      onClick={(e) => {
        e.stopPropagation();
        setEditing(true);
      }}
      title={truncate ? value : undefined}
      className={`w-full rounded underline decoration-dotted decoration-slate-300 underline-offset-2 hover:text-brand-700 disabled:no-underline ${textClass} ${layout}`}
    >
      {label}
    </button>
  );
}

/** Click-to-edit select cell. `display` is the read-only rendering (e.g. a Badge). */
function SelectCell({
  value,
  options,
  display,
  canEdit,
  saving,
  onCommit,
}: {
  value: string;
  options: { value: string; label: string }[];
  display: ReactNode;
  canEdit: boolean;
  saving: boolean;
  onCommit: (next: string) => void;
}): JSX.Element {
  const [editing, setEditing] = useState(false);
  if (editing && canEdit) {
    return (
      <select
        autoFocus
        defaultValue={value}
        onClick={(e) => e.stopPropagation()}
        onBlur={() => setEditing(false)}
        onChange={(e) => {
          if (e.target.value !== value) onCommit(e.target.value);
          setEditing(false);
        }}
        className={INLINE_CONTROL}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    );
  }
  if (!canEdit) return <>{display}</>;
  return (
    <button
      type="button"
      disabled={saving}
      onClick={(e) => {
        e.stopPropagation();
        setEditing(true);
      }}
      className="rounded text-left underline decoration-dotted decoration-slate-300 underline-offset-2 hover:opacity-80 disabled:no-underline"
    >
      {display}
    </button>
  );
}

export function ProductsPage(): JSX.Element {
  const { can } = useAuth();
  const canEdit = can('products', 'edit');

  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<ProductFilters>(EMPTY_FILTERS);
  const [applied, setApplied] = useState<ProductFilters>(EMPTY_FILTERS);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Product | null>(null);

  const categoriesQuery = useServiceCategoryOptions();
  const categories = categoriesQuery.data ?? [];
  const updateProduct = useUpdateProduct();
  const quickSave = (id: string, input: UpdateProductInput): void => {
    updateProduct.mutate({ id, input });
  };

  // Debounce filter changes so typing doesn't fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => {
      setApplied(filters);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [filters]);

  const query = useProducts({
    page,
    pageSize: DEFAULT_PAGE_SIZE,
    sku: applied.sku || undefined,
    name: applied.name || undefined,
    psaClass: applied.psaClass || undefined,
    customerDescription: applied.customerDescription || undefined,
    serviceCategoryId: applied.serviceCategoryId || undefined,
    qboClass: applied.qboClass || undefined,
    billingTiming: applied.billingTiming || undefined,
    isActive: applied.isActive === '' ? undefined : applied.isActive === 'active',
  });

  const products = query.data?.data ?? [];
  const pageInfo = query.data?.page;
  const hasFilters = Object.values(applied).some((v) => v !== '');

  const setFilter = (key: keyof ProductFilters, value: string): void => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const openCreate = (): void => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (product: Product): void => {
    setEditing(product);
    setFormOpen(true);
  };

  return (
    <div>
      <PageHeader
        title="Products"
        description="Manage the catalog of products and services you sell."
        actions={
          canEdit ? (
            <Button onClick={openCreate}>
              <Icon name="products" className="h-4 w-4" />
              New product
            </Button>
          ) : undefined
        }
      />

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        {updateProduct.isError ? (
          <div className="border-b border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-700">
            Could not save the change. Please try again.
          </div>
        ) : null}
        <table className="w-full min-w-[1100px] text-left text-sm">
          <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3 font-medium">SKU</th>
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">PSA class</th>
              <th className="px-4 py-3 font-medium">Customer description</th>
              <th className="px-4 py-3 font-medium">Service category</th>
              <th className="px-4 py-3 font-medium">QBO class</th>
              <th className="px-4 py-3 font-medium">Billing timing</th>
              <th className="px-4 py-3 text-right font-medium">Unit price</th>
              <th className="px-4 py-3 font-medium">Status</th>
            </tr>
            <tr className="border-b border-slate-200">
              <th className="px-3 py-2">
                <input
                  className={FILTER_INPUT}
                  value={filters.sku}
                  onChange={(e) => setFilter('sku', e.target.value)}
                  placeholder="Filter…"
                />
              </th>
              <th className="px-3 py-2">
                <input
                  className={FILTER_INPUT}
                  value={filters.name}
                  onChange={(e) => setFilter('name', e.target.value)}
                  placeholder="Filter…"
                />
              </th>
              <th className="px-3 py-2">
                <select
                  className={FILTER_INPUT}
                  value={filters.psaClass}
                  onChange={(e) => setFilter('psaClass', e.target.value)}
                >
                  <option value="">All</option>
                  {PSA_CLASSES.map((c) => (
                    <option key={c} value={c}>
                      {PSA_CLASS_LABELS[c]}
                    </option>
                  ))}
                </select>
              </th>
              <th className="px-3 py-2">
                <input
                  className={FILTER_INPUT}
                  value={filters.customerDescription}
                  onChange={(e) => setFilter('customerDescription', e.target.value)}
                  placeholder="Filter…"
                />
              </th>
              <th className="px-3 py-2">
                <select
                  className={FILTER_INPUT}
                  value={filters.serviceCategoryId}
                  onChange={(e) => setFilter('serviceCategoryId', e.target.value)}
                >
                  <option value="">All</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </th>
              <th className="px-3 py-2">
                <input
                  className={FILTER_INPUT}
                  value={filters.qboClass}
                  onChange={(e) => setFilter('qboClass', e.target.value)}
                  placeholder="Filter…"
                />
              </th>
              <th className="px-3 py-2">
                <select
                  className={FILTER_INPUT}
                  value={filters.billingTiming}
                  onChange={(e) => setFilter('billingTiming', e.target.value)}
                >
                  <option value="">All</option>
                  {BILLING_TIMINGS.map((t) => (
                    <option key={t} value={t}>
                      {BILLING_TIMING_LABELS[t]}
                    </option>
                  ))}
                </select>
              </th>
              <th className="px-3 py-2" />
              <th className="px-3 py-2">
                <select
                  className={FILTER_INPUT}
                  value={filters.isActive}
                  onChange={(e) => setFilter('isActive', e.target.value)}
                >
                  <option value="">All</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            <DataState
              colSpan={9}
              isLoading={query.isLoading}
              isError={query.isError}
              error={query.error}
              onRetry={() => void query.refetch()}
              isEmpty={products.length === 0}
              emptyTitle="No products found"
              emptyDescription={
                hasFilters
                  ? 'Try adjusting the filters.'
                  : 'Create your first product to get started.'
              }
              emptyAction={
                canEdit && !hasFilters ? (
                  <Button onClick={openCreate}>New product</Button>
                ) : undefined
              }
            >
              {products.map((product) => (
                <tr
                  key={product.id}
                  className="cursor-pointer hover:bg-slate-50"
                  onClick={() => openEdit(product)}
                >
                  <td className="px-4 py-3">
                    <TextCell
                      value={product.sku}
                      canEdit={canEdit}
                      saving={updateProduct.isPending}
                      required
                      mono
                      onCommit={(next) => quickSave(product.id, { sku: next.trim() })}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <TextCell
                      value={product.name}
                      canEdit={canEdit}
                      saving={updateProduct.isPending}
                      required
                      bold
                      onCommit={(next) => quickSave(product.id, { name: next.trim() })}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <SelectCell
                      value={product.psaClass ?? ''}
                      canEdit={canEdit}
                      saving={updateProduct.isPending}
                      options={[
                        { value: '', label: 'None' },
                        ...PSA_CLASSES.map((c) => ({ value: c, label: PSA_CLASS_LABELS[c] })),
                      ]}
                      display={
                        <span className="text-slate-600">
                          {product.psaClass ? PSA_CLASS_LABELS[product.psaClass] : '—'}
                        </span>
                      }
                      onCommit={(v) =>
                        quickSave(product.id, { psaClass: v ? (v as PsaClass) : null })
                      }
                    />
                  </td>
                  <td className="px-4 py-3">
                    <TextCell
                      value={product.customerDescription ?? ''}
                      canEdit={canEdit}
                      saving={updateProduct.isPending}
                      truncate
                      placeholder="Customer description"
                      onCommit={(next) =>
                        quickSave(product.id, { customerDescription: next.trim() || null })
                      }
                    />
                  </td>
                  <td className="px-4 py-3">
                    <SelectCell
                      value={product.serviceCategoryId ?? ''}
                      canEdit={canEdit}
                      saving={updateProduct.isPending}
                      options={[
                        { value: '', label: 'None' },
                        ...categories.map((category) => ({
                          value: category.id,
                          label: category.name,
                        })),
                      ]}
                      display={
                        <span className="text-slate-600">
                          {product.serviceCategoryName || '—'}
                        </span>
                      }
                      onCommit={(v) => quickSave(product.id, { serviceCategoryId: v || null })}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <TextCell
                      value={product.qboClass ?? ''}
                      canEdit={canEdit}
                      saving={updateProduct.isPending}
                      placeholder="QBO class"
                      onCommit={(next) => quickSave(product.id, { qboClass: next.trim() || null })}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <SelectCell
                      value={product.billingTiming}
                      canEdit={canEdit}
                      saving={updateProduct.isPending}
                      options={BILLING_TIMINGS.map((t) => ({
                        value: t,
                        label: BILLING_TIMING_LABELS[t],
                      }))}
                      display={
                        <span className="text-slate-600">
                          {BILLING_TIMING_LABELS[product.billingTiming]}
                        </span>
                      }
                      onCommit={(v) =>
                        quickSave(product.id, { billingTiming: v as BillingTiming })
                      }
                    />
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    <TextCell
                      value={product.unitPrice}
                      canEdit={canEdit}
                      saving={updateProduct.isPending}
                      required
                      align="right"
                      onCommit={(next) => quickSave(product.id, { unitPrice: next.trim() })}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <SelectCell
                      value={product.isActive ? 'active' : 'inactive'}
                      canEdit={canEdit}
                      saving={updateProduct.isPending}
                      options={[
                        { value: 'active', label: 'Active' },
                        { value: 'inactive', label: 'Inactive' },
                      ]}
                      display={
                        <Badge tone={product.isActive ? 'green' : 'gray'}>
                          {product.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      }
                      onCommit={(v) => quickSave(product.id, { isActive: v === 'active' })}
                    />
                  </td>
                </tr>
              ))}
            </DataState>
          </tbody>
        </table>

        {pageInfo && products.length > 0 ? (
          <Pagination
            page={pageInfo.page}
            pageSize={pageInfo.pageSize}
            totalItems={pageInfo.totalItems}
            totalPages={pageInfo.totalPages}
            onPageChange={setPage}
          />
        ) : null}
      </div>

      <ProductFormModal isOpen={formOpen} product={editing} onClose={() => setFormOpen(false)} />
    </div>
  );
}
